import { useCallback, useEffect, useRef, useState } from "react"
import {
  Box,
  Drawer,
  Flex,
  HStack,
  IconButton,
  Portal,
  Spinner,
  Stack,
} from "@chakra-ui/react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { LuChevronDown, LuChevronRight, LuRefreshCw, LuX } from "react-icons/lu"
import { Button, Text } from "@/components/ui"
import {
  getCompanyAssessment,
  runCompanyAssessment,
  CompanyAssessment,
  AssessmentScores,
  ScorecardCategory,
  ScorecardSignal,
  Tier,
  SIGNAL_LABELS,
} from "@/api/companies"

// ─── Types ────────────────────────────────────────────────────────────────────

interface Props {
  companyId: number
  companyName: string
  isOpen: boolean
  onClose: () => void
  onAssessmentComplete?: () => void
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const TABLE_STYLE: React.CSSProperties = {
  borderCollapse: "collapse",
  width: "100%",
  margin: "16px 0 24px 0",
  fontSize: "12px",
}
const TH_STYLE: React.CSSProperties = {
  background: "rgba(255,255,255,0.10)",
  fontWeight: 700,
  fontSize: "11px",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  padding: "8px 16px",
  textAlign: "left",
  borderBottom: "2px solid rgba(255,255,255,0.25)",
}
const TD_STYLE: React.CSSProperties = {
  padding: "10px 16px",
  verticalAlign: "top",
  lineHeight: "1.5",
  borderBottom: "1px solid rgba(255,255,255,0.10)",
}

const markdownComponents = {
  table: ({ children }: { children?: React.ReactNode }) => (
    <table style={TABLE_STYLE}>{children}</table>
  ),
  th: ({ children }: { children?: React.ReactNode }) => (
    <th style={TH_STYLE}>{children}</th>
  ),
  td: ({ children }: { children?: React.ReactNode }) => (
    <td style={TD_STYLE}>{children}</td>
  ),
  hr: () => (
    <hr style={{ border: "none", borderTop: "1px solid rgba(255,255,255,0.18)", margin: "20px 0" }} />
  ),
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return "just now"
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  return `${Math.floor(days / 7)}w ago`
}

function tierColor(tier: Tier | string): string {
  switch (tier) {
    case "A": return "#22c55e"
    case "B": return "#3b82f6"
    case "C": return "#f59e0b"
    case "D": return "#ef4444"
    default: return "#6b7280"
  }
}

function tierBg(tier: Tier | string): string {
  switch (tier) {
    case "A": return "rgba(34,197,94,0.15)"
    case "B": return "rgba(59,130,246,0.15)"
    case "C": return "rgba(245,158,11,0.15)"
    case "D": return "rgba(239,68,68,0.15)"
    default: return "rgba(107,114,128,0.15)"
  }
}

function scoreColor(score: number | null): string {
  if (score === null) return "#6b7280"
  if (score >= 8) return "#22c55e"
  if (score >= 6) return "#3b82f6"
  if (score >= 4) return "#f59e0b"
  return "#ef4444"
}

function estimateCost(inputTokens: number | null, outputTokens: number | null): string {
  if (!inputTokens && !outputTokens) return ""
  const inp = inputTokens ?? 0
  const out = outputTokens ?? 0
  const cost = (inp * 3.0 + out * 15.0) / 1_000_000
  return `~$${cost.toFixed(3)}`
}

/** Check if scores use the new 4-category format */
function isNewFormat(scores: AssessmentScores | Record<string, unknown>): scores is AssessmentScores {
  return scores && Array.isArray((scores as AssessmentScores).categories)
}

// ─── Tier Badge ──────────────────────────────────────────────────────────────

function TierBadge({ tier, overallScore }: { tier: Tier | string; overallScore: number }) {
  return (
    <HStack gap={2}>
      <Box
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        w="36px"
        h="36px"
        borderRadius="8px"
        style={{
          backgroundColor: tierBg(tier),
          border: `2px solid ${tierColor(tier)}`,
          color: tierColor(tier),
          fontSize: "20px",
          fontWeight: 800,
        }}
      >
        {tier}
      </Box>
      <Box>
        <Text fontSize="18px" fontWeight="700" lineHeight="1" style={{ color: tierColor(tier) }}>
          {overallScore}
          <Text as="span" fontSize="12px" fontWeight="500" color="text.muted">/100</Text>
        </Text>
      </Box>
    </HStack>
  )
}

// ─── Greatness Badge ────────────────────────────────────────────────────────

function GreatnessBadge() {
  return (
    <Box
      display="inline-flex"
      alignItems="center"
      px={3}
      py={1}
      borderRadius="full"
      style={{
        backgroundColor: "rgba(234,179,8,0.12)",
        border: "1.5px solid rgba(234,179,8,0.6)",
        color: "#eab308",
        fontSize: "11px",
        fontWeight: 700,
        letterSpacing: "0.06em",
        textTransform: "uppercase" as const,
      }}
    >
      Signs of Greatness
    </Box>
  )
}

// ─── Cap Table Gate Warning ─────────────────────────────────────────────────

function CapTableGateWarning() {
  return (
    <Box
      px={4}
      py={3}
      borderRadius="8px"
      bg="rgba(239,68,68,0.08)"
      border="1px solid rgba(239,68,68,0.3)"
      mb={3}
    >
      <Text fontSize="12px" fontWeight="700" color="#ef4444" mb={1}>
        CAP TABLE GATE WARNING
      </Text>
      <Text fontSize="12px" color="text.primary" lineHeight="1.5">
        Despite the overall score, the cap table structure poses significant risk to employee
        common stock value at exit. Review the preference stack before proceeding.
      </Text>
    </Box>
  )
}

// ─── Category Row ───────────────────────────────────────────────────────────

function CategoryRow({ category }: { category: ScorecardCategory }) {
  const [expanded, setExpanded] = useState(false)
  const color = scoreColor(category.categoryScore)
  const barWidth = Math.min(100, category.categoryScore * 10)

  return (
    <Box
      bg="elevated.bg"
      borderRadius="8px"
      border="1px solid"
      borderColor="border.default"
      mb={2}
    >
      {/* Category header — clickable to expand */}
      <HStack
        px={3}
        py={2.5}
        cursor="pointer"
        onClick={() => setExpanded(!expanded)}
        _hover={{ opacity: 0.85 }}
      >
        <Box color="text.muted" flexShrink={0}>
          {expanded ? <LuChevronDown size={14} /> : <LuChevronRight size={14} />}
        </Box>
        <Box flex={1}>
          <HStack justify="space-between">
            <Text fontSize="12px" fontWeight="600" color="text.primary">
              {category.label}
            </Text>
            <HStack gap={2}>
              <Text fontSize="10px" color="text.muted" fontWeight="500">
                {Math.round(category.weight * 100)}%
              </Text>
              <Text fontSize="14px" fontWeight="700" style={{ color }}>
                {category.categoryScore.toFixed(1)}
              </Text>
            </HStack>
          </HStack>
          {/* Score bar */}
          <Box mt={1.5} h="3px" bg="border.default" borderRadius="full">
            <Box
              h="3px"
              borderRadius="full"
              style={{ width: `${barWidth}%`, backgroundColor: color, transition: "width 0.3s" }}
            />
          </Box>
        </Box>
      </HStack>

      {/* Expanded signals */}
      {expanded && (
        <Box px={4} pb={3} pt={0}>
          {category.signals.map((signal) => (
            <SignalRow key={signal.key} signal={signal} />
          ))}
        </Box>
      )}
    </Box>
  )
}

// ─── Signal Row (inside expanded category) ──────────────────────────────────

function SignalRow({ signal }: { signal: ScorecardSignal }) {
  const label = SIGNAL_LABELS[signal.key] ?? signal.key
  const color = scoreColor(signal.score)

  return (
    <HStack
      py={1.5}
      borderBottom="1px solid"
      borderColor="border.default"
      _last={{ borderBottom: "none" }}
      align="flex-start"
    >
      <Text
        fontSize="13px"
        fontWeight="700"
        minW="28px"
        style={{ color }}
      >
        {signal.score !== null ? signal.score : "—"}
      </Text>
      <Box flex={1}>
        <Text fontSize="11px" fontWeight="600" color="text.primary">
          {label}
        </Text>
        <Text fontSize="11px" color="text.muted" lineHeight="1.4" mt={0.5}>
          {signal.rationale}
        </Text>
      </Box>
    </HStack>
  )
}

// ─── Key Strengths / Risks ──────────────────────────────────────────────────

function KeySignals({
  strengths,
  risks,
}: {
  strengths: ScorecardSignal[]
  risks: ScorecardSignal[]
}) {
  if (strengths.length === 0 && risks.length === 0) return null

  return (
    <HStack align="flex-start" gap={4} mb={3}>
      {strengths.length > 0 && (
        <Box flex={1}>
          <Text fontSize="10px" fontWeight="700" color="#22c55e" textTransform="uppercase" letterSpacing="0.05em" mb={1}>
            Strengths
          </Text>
          {strengths.slice(0, 4).map((s) => (
            <HStack key={s.key} gap={1.5} mb={0.5}>
              <Text fontSize="11px" fontWeight="700" color="#22c55e">{s.score}</Text>
              <Text fontSize="11px" color="text.muted">{SIGNAL_LABELS[s.key] ?? s.key}</Text>
            </HStack>
          ))}
        </Box>
      )}
      {risks.length > 0 && (
        <Box flex={1}>
          <Text fontSize="10px" fontWeight="700" color="#ef4444" textTransform="uppercase" letterSpacing="0.05em" mb={1}>
            Risks
          </Text>
          {risks.slice(0, 4).map((s) => (
            <HStack key={s.key} gap={1.5} mb={0.5}>
              <Text fontSize="11px" fontWeight="700" color="#ef4444">{s.score}</Text>
              <Text fontSize="11px" color="text.muted">{SIGNAL_LABELS[s.key] ?? s.key}</Text>
            </HStack>
          ))}
        </Box>
      )}
    </HStack>
  )
}

// ─── Legacy 5-card view (backward compat for old assessments) ───────────────

const LEGACY_LABELS: Record<string, string> = {
  liquidity: "Liquidity",
  businessHealth: "Business",
  teamStability: "Team",
  market: "Market",
  esoRisk: "ESO Risk",
}

function LegacyScoreCards({ scores }: { scores: Record<string, unknown> }) {
  const keys = Object.keys(LEGACY_LABELS)
  return (
    <HStack gap={2} mb={4} flexWrap="wrap">
      {keys.map((k) => {
        const score = Number(scores[k] ?? 5)
        const color = scoreColor(score)
        return (
          <Box
            key={k}
            bg="elevated.bg"
            borderRadius={8}
            border="1px solid"
            borderColor="border.default"
            p={3}
            textAlign="center"
            flex="1"
            minW="60px"
          >
            <Text fontSize="22px" fontWeight="700" lineHeight="1" style={{ color }}>
              {score}
            </Text>
            <Text fontSize="10px" color="text.muted" fontWeight="600" textTransform="uppercase" letterSpacing="0.05em" mt={1}>
              {LEGACY_LABELS[k]}
            </Text>
          </Box>
        )
      })}
    </HStack>
  )
}

// ─── Main Panel ───────────────────────────────────────────────────────────────

export const CompanyAssessmentPanel = ({ companyId, companyName, isOpen, onClose, onAssessmentComplete }: Props) => {
  const [assessment, setAssessment] = useState<CompanyAssessment | null>(null)
  const [streamedText, setStreamedText] = useState("")
  const [status, setStatus] = useState("")
  const [isRunning, setIsRunning] = useState(false)
  const [stale, setStale] = useState(false)
  const [stalenessReason, setStalenessReason] = useState<string | undefined>()
  const abortRef = useRef<AbortController | null>(null)
  const isRunningRef = useRef(false)
  const contentRef = useRef<HTMLDivElement>(null)

  const scrollToBottom = useCallback(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight
    }
  }, [])

  // Keep ref in sync with state (avoids dependency issues in other effects)
  useEffect(() => { isRunningRef.current = isRunning }, [isRunning])

  // Abort on true unmount (page navigation) only
  useEffect(() => () => { abortRef.current?.abort() }, [])

  // Auto-scroll while streaming
  useEffect(() => {
    if (isRunning) scrollToBottom()
  }, [streamedText, isRunning, scrollToBottom])

  const startAssessment = useCallback(async () => {
    abortRef.current?.abort()
    const ctrl = new AbortController()
    abortRef.current = ctrl

    setIsRunning(true)
    setStreamedText("")
    setStatus("Preparing assessment...")
    setAssessment(null)

    try {
      const result = await runCompanyAssessment(companyId, ctrl.signal, (event) => {
        if (event.type === "status") {
          setStatus(event.text)
        } else if (event.type === "token") {
          setStreamedText((prev) => prev + event.text)
        } else if (event.type === "done") {
          setAssessment(event.assessment)
          setStale(false)
          setStalenessReason(undefined)
          setStreamedText("")
          setStatus("")
          onAssessmentComplete?.()
        } else if (event.type === "error") {
          setStatus(`Error: ${event.message}`)
        }
      })
      if (result) {
        setAssessment(result)
        setStale(false)
      }
    } catch (err: unknown) {
      if (err instanceof Error && err.name !== "AbortError") {
        setStatus("Assessment failed — please try again")
      }
    } finally {
      setIsRunning(false)
    }
  }, [companyId])

  // On open: fetch cached assessment, auto-run if none
  useEffect(() => {
    if (!isOpen) return
    if (isRunningRef.current) return // assessment already in progress, don't restart
    let cancelled = false

    const init = async () => {
      try {
        const { assessment: cached, stale: isStale, stalenessReason: reason } =
          await getCompanyAssessment(companyId)

        if (cancelled) return

        if (cached) {
          setAssessment(cached)
          setStale(isStale)
          setStalenessReason(reason)
        }

        if (!cached) {
          startAssessment()
        }
      } catch {
        if (!cancelled) startAssessment()
      }
    }

    init()
    return () => { cancelled = true }
  }, [isOpen, companyId, startAssessment])

  const displayContent = assessment?.content ?? streamedText
  const scores = assessment?.scores
  const hasNewFormat = scores && isNewFormat(scores)

  return (
    <Drawer.Root
      open={isOpen}
      onOpenChange={(e) => { if (!e.open) onClose() }}
      placement="end"
      size="full"
    >
      <Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content
            bg="card.bg"
            display="flex"
            flexDirection="column"
            w="60vw"
            maxW="860px"
          >
            {/* Header */}
            <HStack
              px={5}
              py={3}
              borderBottom="1px solid"
              borderColor="border.default"
              justify="space-between"
              flexShrink={0}
            >
              <HStack gap={3}>
                <Text fontWeight="700" fontSize="15px" color="text.primary">
                  Company Scorecard
                </Text>
                <Text fontSize="13px" color="text.muted">
                  {companyName}
                </Text>
              </HStack>
              <HStack gap={2}>
                {!isRunning && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={startAssessment}
                    disabled={isRunning}
                  >
                    <LuRefreshCw size={14} style={{ marginRight: 4 }} />
                    Refresh
                  </Button>
                )}
                <IconButton
                  aria-label="Close"
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                >
                  <LuX />
                </IconButton>
              </HStack>
            </HStack>

            {/* Status bar */}
            {(isRunning || status) && (
              <HStack px={5} py={2} gap={2} borderBottom="1px solid" borderColor="border.default" flexShrink={0}>
                {isRunning && <Spinner size="xs" color="accent.default" />}
                <Text fontSize="13px" color="text.muted">{status}</Text>
              </HStack>
            )}

            {/* Scrollable body — scores + content together */}
            <Box
              ref={contentRef}
              flex={1}
              overflowY="auto"
            >
              {/* Scores section — only when assessment is ready */}
              {assessment && scores && (
                <Box px={5} pt={4} pb={2} borderBottom="1px solid" borderColor="border.default">
                  {/* Tier + badges row */}
                  <HStack justify="space-between" mb={3} flexWrap="wrap" gap={2}>
                    <HStack gap={3}>
                      {hasNewFormat ? (
                        <TierBadge tier={scores.tier} overallScore={scores.overallScore} />
                      ) : (
                        <Box
                          display="inline-flex"
                          alignItems="center"
                          px={3}
                          py={1}
                          borderRadius="full"
                          style={{
                            backgroundColor: "rgba(59,130,246,0.15)",
                            border: "1.5px solid rgba(59,130,246,0.6)",
                            color: "#3b82f6",
                            fontSize: "12px",
                            fontWeight: 700,
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                          }}
                        >
                          {assessment.signal}
                        </Box>
                      )}
                      {hasNewFormat && scores.isGreat && <GreatnessBadge />}
                    </HStack>
                    <HStack gap={2}>
                      {stale && (
                        <Box
                          px={2}
                          py={0.5}
                          borderRadius="full"
                          bg="rgba(245,158,11,0.12)"
                          border="1px solid rgba(245,158,11,0.4)"
                        >
                          <Text fontSize="11px" color="#f59e0b" fontWeight="600">
                            Stale{stalenessReason ? ` — ${stalenessReason}` : ""}
                          </Text>
                        </Box>
                      )}
                      <Text fontSize="11px" color="text.muted">
                        {timeAgo(assessment.updatedAt)}
                        {assessment.inputTokens && (
                          <> · {estimateCost(assessment.inputTokens, assessment.outputTokens)}</>
                        )}
                      </Text>
                    </HStack>
                  </HStack>

                  {/* Cap table gate warning */}
                  {hasNewFormat && scores.capTableGateFailed && <CapTableGateWarning />}

                  {/* Investment one-liner */}
                  {assessment.investmentOneLiner && (
                    <Box
                      px={4}
                      py={2.5}
                      mb={3}
                      borderRadius="8px"
                      bg="rgba(59,130,246,0.06)"
                      border="1px solid rgba(59,130,246,0.15)"
                    >
                      <Text fontSize="13px" color="text.primary" fontWeight="500" fontStyle="italic" lineHeight="1.5">
                        {assessment.investmentOneLiner}
                      </Text>
                    </Box>
                  )}

                  {/* Category scores or legacy cards */}
                  {hasNewFormat ? (
                    <>
                      {scores.categories.map((cat) => (
                        <CategoryRow key={cat.key} category={cat} />
                      ))}
                      <KeySignals
                        strengths={scores.keyStrengths}
                        risks={scores.keyRisks}
                      />
                    </>
                  ) : (
                    <>
                      <LegacyScoreCards scores={scores as unknown as Record<string, unknown>} />
                      <Box
                        px={3}
                        py={2}
                        mb={3}
                        borderRadius="6px"
                        bg="rgba(59,130,246,0.06)"
                        border="1px solid rgba(59,130,246,0.2)"
                      >
                        <Text fontSize="11px" color="text.muted">
                          This assessment uses the legacy 5-dimension format. Click <strong>Refresh</strong> to run the new 18-signal scorecard.
                        </Text>
                      </Box>
                    </>
                  )}
                </Box>
              )}

              {/* Content */}
              <Box
                px={6}
                py={5}
                fontSize="13px"
                lineHeight="1.7"
                color="text.primary"
              >
                {!displayContent && !isRunning && (
                  <Flex h="200px" align="center" justify="center">
                    <Stack align="center" gap={3}>
                      <Spinner size="lg" color="accent.default" />
                      <Text color="text.muted" fontSize="14px">Loading assessment...</Text>
                    </Stack>
                  </Flex>
                )}
                {displayContent && (
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={markdownComponents as Record<string, unknown>}
                  >
                    {displayContent}
                  </ReactMarkdown>
                )}
                {isRunning && streamedText && (
                  <Box
                    display="inline-block"
                    w="2px"
                    h="1em"
                    bg="accent.default"
                    style={{ animation: "blink 1s step-end infinite", verticalAlign: "text-bottom" }}
                  />
                )}
              </Box>
            </Box>

            {/* Footer: stale nudge */}
            {assessment && stale && !isRunning && (
              <Box
                px={5}
                py={3}
                borderTop="1px solid"
                borderColor="border.default"
                bg="rgba(245,158,11,0.06)"
                flexShrink={0}
              >
                <HStack justify="space-between">
                  <Text fontSize="12px" color="text.muted">
                    {stalenessReason ?? "Assessment may be outdated."}
                  </Text>
                  <Button size="sm" variant="outline" onClick={startAssessment}>
                    Run fresh assessment
                  </Button>
                </HStack>
              </Box>
            )}
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  )
}
